<template>
  <div
    id="app"
    :class="{ 'has-activity-banner': showActivityBanner }"
    :style="{
      '--site-icp-footer-height': showIcpFooter ? '36PX' : '0PX',
      '--activity-banner-height': showActivityBanner ? '48PX' : '0PX',
    }"
  >
    <activity-promotion-banner
      v-if="showActivityBanner"
      :activity="activityBannerData"
      @action="handleActivityBannerAction"
      @close="dismissActivityBanner"
    />
    <div
      v-if="showActivityBanner"
      class="activity-promotion-spacer"
      aria-hidden="true"
    ></div>
    <!-- 登录/邀请落地页不挂全局顶栏，避免顶部再露出站点 logo -->
    <headDer v-if="showAppHeader"></headDer>
    <router-view />
    <div v-if="showFirstLoginAllocationPrompt" class="first-login-allocation">
      <div
        v-if="allocationGuideTargetStyle"
        class="first-login-allocation__spotlight"
        :style="allocationGuideTargetStyle"
      ></div>
      <div class="first-login-allocation__dialog" @click.stop>
        <h2 class="first-login-allocation__title">
          {{ $t("app.firstLogin.title") }}
        </h2>
        <div class="first-login-allocation__desc">
          <p>{{ $t("app.firstLogin.welcome") }}</p>
          <i18n path="app.firstLogin.gift" tag="p">
            <span place="action" class="first-login-allocation__emphasis">
              {{ $t("app.firstLogin.action") }}
            </span>
          </i18n>
          <p>{{ $t("app.firstLogin.followUp") }}</p>
        </div>
        <button
          type="button"
          class="first-login-allocation__primary"
          @click="handleFirstLoginAllocate"
        >
          {{ $t("app.firstLogin.allocate") }}
        </button>
        <button
          type="button"
          class="first-login-allocation__secondary"
          @click="dismissFirstLoginAllocation"
        >
          {{ $t("app.firstLogin.dismiss") }}
        </button>
      </div>
    </div>
    <!-- 登录未读公告：排在首次分配引导之后展示 -->
    <login-announcement-dialog
      :visible.sync="loginAnnouncementVisible"
      :announcement="loginAnnouncement"
      @confirm="handleLoginAnnouncementConfirm"
      @closed="handleLoginAnnouncementConfirm"
    />
    <footer v-if="showIcpFooter" class="site-icp-footer">
      <a class="site-icp-link" target="_blank" rel="noopener noreferrer">
        {{ icpNumber }}
      </a>
    </footer>
  </div>
</template>
<script>
import headDer from "@/components/head.vue";
import ActivityPromotionBanner from "@/components/ActivityPromotionBanner.vue";
import LoginAnnouncementDialog from "@/components/LoginAnnouncementDialog.vue";
import bus from "@/bus.js";
import { canPurchaseMembership, isSuperAdmin } from "@/utils/accountRole";
import { isSubscriptionButtonHidden } from "@/utils/subscription";
import {
  clearPendingAnnouncement,
  peekPendingAnnouncement,
} from "@/utils/announcement";
import {
  SESSION_REV_KEY,
  rebindSessionIdentity,
  syncSessionIdentity,
} from "@/utils/sessionIdentity";

const TOKEN_RENEW_INTERVAL = 10 * 60 * 1000;
const ACTIVITY_BANNER_DISMISSED_KEY = "activityPromotionBannerDismissedV1";
const PENDING_ACTIVITY_KEY = "pendingActivityPromotion";

function getActivityBannerDateKey() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(
    now.getDate(),
  ).padStart(2, "0")}`;
}

export default {
  components: {
    headDer,
    ActivityPromotionBanner,
    LoginAnnouncementDialog,
  },
  data() {
    return {
      footerHiddenRoutePrefixes: [
        "/login",
        "/team-invite",
        "/project-group/invite",
        "/project-group-invite",
        "/visionCanvas/editor",
      ],
      showFirstLoginAllocationPrompt: false,
      previousBodyOverflow: null,
      allocationGuideTargetStyle: null,
      allocationGuideResizeHandler: null,
      tokenRenewTimer: null,
      tokenRenewing: false,
      // 登录接口带回的未读公告弹窗状态
      loginAnnouncementVisible: false,
      loginAnnouncement: null,
      activityBannerDismissed:
        typeof window !== "undefined" &&
        window.localStorage.getItem(ACTIVITY_BANNER_DISMISSED_KEY) ===
          getActivityBannerDateKey(),
      activityBannerResetTimer: null,
      activityBannerDismissedDate:
        typeof window !== "undefined"
          ? window.localStorage.getItem(ACTIVITY_BANNER_DISMISSED_KEY) || ""
          : "",
      activityPromotion: {
        activityName: "",
        mainTitle: "",
        subTitle: "",
        endDate: "",
        periods: [],
      },
      activityPromotionRequestState: null,
      activityPromotionRequestLoading: false,
      authToken: window.localStorage.getItem("token") || "",
    };
  },
  computed: {
    showActivityBanner() {
      const hiddenRoutes = ["/team-invite", "/visionCanvas/editor"];
      return (
        process.env.VUE_APP_LOGO_IMAGE === "logo-manyu" &&
        Boolean(this.authToken) &&
        this.canShowSubscriptionEntry &&
        !this.activityBannerDismissed &&
        (Boolean(this.activityBannerData.mainTitle) ||
          Boolean(this.activityBannerData.subTitle) ||
          (Array.isArray(this.activityBannerData.periods) &&
            this.activityBannerData.periods.length > 0)) &&
        !hiddenRoutes.some(
          (path) =>
            this.$route.path === path || this.$route.path.startsWith(`${path}/`),
        )
      );
    },
    icpNumber() {
      return process.env.VUE_APP_ICP_NUMBER || "";
    },
    activityBannerData() {
      const meta = this.$store.state.subscriptionPlanMeta || {};
      return {
        ...this.activityPromotion,
        activityName: this.activityPromotion.activityName || meta.activityName || "",
        mainTitle: this.activityPromotion.mainTitle || meta.mainTitle || "",
        subTitle: this.activityPromotion.subTitle || meta.subTitle || "",
        endDate: this.activityPromotion.endDate || meta.endDate || "",
      };
    },
    canShowSubscriptionEntry() {
      const user = this.getStoredUser();
      const meta = this.$store.state.subscriptionPlanMeta || {};
      return Boolean(
        meta.activityName &&
          !this.$store.state.isCloseVip &&
          canPurchaseMembership(user) &&
          !isSubscriptionButtonHidden(user),
      );
    },
    showIcpFooter() {
      if (!this.icpNumber) {
        return false;
      }
      return !this.footerHiddenRoutePrefixes.some(
        (path) =>
          this.$route.path === path || this.$route.path.startsWith(`${path}/`),
      );
    },
    // 登录 / 团队邀请 / 项目组邀请落地页不弹首次分配和登录公告
    isStandaloneAuthRoute() {
      const path = (this.$route && this.$route.path) || "";
      return (
        path === "/login" ||
        path === "/team-invite" ||
        path === "/project-group/invite" ||
        path === "/project-group-invite"
      );
    },
    // 独立落地页或路由标记 hideHeader 时都不渲染全局顶栏
    showAppHeader() {
      if (this.isStandaloneAuthRoute) return false;
      return !(this.$route.meta && this.$route.meta.hideHeader);
    },
  },
  watch: {
    "$route.path": {
      immediate: true,
      handler() {
        this.checkFirstLoginAllocationPrompt();
        this.syncTokenRenewTimer();
        this.updateDocumentTitle();
        // 首次分配引导优先；无引导时再尝试展示登录公告
        this.$nextTick(() => {
          this.tryShowLoginAnnouncement();
        });
      },
    },
    "$i18n.locale"() {
      this.updateDocumentTitle();
    },
    showFirstLoginAllocationPrompt(visible) {
      this.updateBodyScrollLock();
      this.updateAllocationGuideState();
      // 用户关闭首次分配引导后再弹出登录公告，避免叠层
      if (!visible) {
        this.$nextTick(() => {
          this.tryShowLoginAnnouncement();
        });
      }
    },
  },
  mounted() {
    this.fetchActivityPromotion();
    this.scheduleActivityBannerReset();
    if (typeof window !== "undefined") {
      // 本窗口改登录态只重绑；其它窗口写入走 storage
      window.addEventListener("auth-token-changed", this.handleAuthTokenChanged);
      window.addEventListener("storage", this.handleTokenStorageChange);
      document.addEventListener(
        "visibilitychange",
        this.handleSessionVisibilityChange,
      );
      // 两个独立窗口并排时 visibility 可能一直 visible，靠 focus 兜底
      window.addEventListener("focus", this.handleSessionWindowFocus);
      document.addEventListener(
        "visibilitychange",
        this.handleActivityBannerVisibility,
      );
    }
    this.syncTokenRenewTimer();
  },
  beforeDestroy() {
    if (typeof window !== "undefined") {
      window.removeEventListener(
        "auth-token-changed",
        this.handleAuthTokenChanged,
      );
      window.removeEventListener("storage", this.handleTokenStorageChange);
      document.removeEventListener(
        "visibilitychange",
        this.handleSessionVisibilityChange,
      );
      window.removeEventListener("focus", this.handleSessionWindowFocus);
      document.removeEventListener(
        "visibilitychange",
        this.handleActivityBannerVisibility,
      );
    }
    this.clearActivityBannerResetTimer();
    this.stopTokenRenewTimer();
    this.releaseBodyScrollLock();
    this.removeAllocationGuideResize();
  },
  methods: {
    clearActivityBannerResetTimer() {
      if (this.activityBannerResetTimer) {
        window.clearTimeout(this.activityBannerResetTimer);
        this.activityBannerResetTimer = null;
      }
    },
    scheduleActivityBannerReset() {
      this.clearActivityBannerResetTimer();
      const now = new Date();
      const nextDay = new Date(now);
      nextDay.setHours(24, 0, 0, 50);
      this.activityBannerResetTimer = window.setTimeout(() => {
        this.refreshActivityBannerDate();
        this.scheduleActivityBannerReset();
      }, Math.max(1000, nextDay.getTime() - now.getTime()));
    },
    refreshActivityBannerDate() {
      const today = getActivityBannerDateKey();
      if (this.activityBannerDismissedDate !== today) {
        this.activityBannerDismissedDate = today;
        this.activityBannerDismissed = false;
      }
    },
    handleActivityBannerVisibility() {
      if (document.visibilityState === "visible") {
        this.refreshActivityBannerDate();
        this.scheduleActivityBannerReset();
      }
    },
    fetchActivityPromotion() {
      if (process.env.VUE_APP_LOGO_IMAGE !== "logo-manyu") {
        return;
      }
      const requestState = this.hasLoginToken() ? "authenticated" : "anonymous";
      if (this.activityPromotionRequestState === requestState) {
        return;
      }
      this.activityPromotionRequestState = requestState;
      this.activityPromotionRequestLoading = true;
      this.$axios
        .get(this.$store.state.serverUrl + "ytb/subscription/plan/tier-range", {
          params: { periodType: 3 },
        })
        .then((res) => {
          if (
            this.hasLoginToken() === (requestState === "authenticated") &&
            res.data &&
            Number(res.data.code) === 200
          ) {
            const data = res.data.data || {};
            const highestPlan =
              data.periods && data.periods[0] && data.periods[0].highest;
            const highestActivity =
              highestPlan &&
              Array.isArray(highestPlan.activities) &&
              highestPlan.activities[0]
                ? highestPlan.activities[0]
                : {};
            this.activityPromotion = {
              activityName: data.activityName || "",
              mainTitle: highestActivity.mainTitle || data.mainTitle || "",
              subTitle: highestActivity.subtitle || data.subTitle || "",
              endDate: data.endDate || "",
              periods: Array.isArray(data.periods) ? data.periods : [],
            };
          }
        })
        .catch(() => {
          this.activityPromotionRequestState = null;
        })
        .finally(() => {
          this.activityPromotionRequestLoading = false;
        });
    },
    dismissActivityBanner() {
      window.localStorage.setItem(
        ACTIVITY_BANNER_DISMISSED_KEY,
        getActivityBannerDateKey(),
      );
      this.activityBannerDismissedDate = getActivityBannerDateKey();
      this.activityBannerDismissed = true;
    },
    handleActivityBannerAction() {
      if (this.hasLoginToken()) {
        bus.$emit("openSubscriptionPromotion");
        return;
      }
      window.sessionStorage.setItem(PENDING_ACTIVITY_KEY, "1");
      if (this.$route.path === "/login") {
        window.dispatchEvent(new CustomEvent("open-activity-login"));
        return;
      }
      this.$router.push("/login");
    },
    updateDocumentTitle() {
      if (typeof document === "undefined") {
        return;
      }
      const titleKey = this.$route.meta && this.$route.meta.titleKey;
      const appTitle =
        process.env.VUE_APP_BROWSER_TITLE || this.$t("app.title");
      document.title = titleKey
        ? `${this.$t(titleKey)} | ${appTitle}`
        : appTitle;
    },
    hasLoginToken() {
      return (
        typeof window !== "undefined" &&
        Boolean(window.localStorage.getItem("token"))
      );
    },
    syncTokenRenewTimer() {
      if (this.hasLoginToken()) {
        this.startTokenRenewTimer();
        return;
      }
      this.stopTokenRenewTimer();
    },
    startTokenRenewTimer() {
      if (this.tokenRenewTimer || !this.hasLoginToken()) {
        return;
      }
      this.tokenRenewTimer = setInterval(() => {
        this.renewToken();
        console.log("renewToken");
      }, TOKEN_RENEW_INTERVAL);
    },
    stopTokenRenewTimer() {
      if (this.tokenRenewTimer) {
        clearInterval(this.tokenRenewTimer);
        this.tokenRenewTimer = null;
      }
      this.tokenRenewing = false;
    },
    handleAuthTokenChanged() {
      this.authToken = window.localStorage.getItem("token") || "";
      // 本窗口登录/退出/续期：重绑身份，避免下一跳路由误判为换账号
      rebindSessionIdentity();
      this.syncTokenRenewTimer();
      this.fetchActivityPromotion();
    },
    handleTokenStorageChange(event) {
      if (
        event &&
        event.key &&
        event.key !== "token" &&
        event.key !== "user" &&
        event.key !== "id" &&
        event.key !== SESSION_REV_KEY
      ) {
        return;
      }
      this.authToken = window.localStorage.getItem("token") || "";
      this.syncTokenRenewTimer();
      this.fetchActivityPromotion();
      // 其它窗口换账号或退出时对齐 header / 页面内存
      syncSessionIdentity();
    },
    handleSessionVisibilityChange() {
      if (document.visibilityState !== "visible") {
        return;
      }
      // 从后台切回时再对一次，避免个别浏览器漏发 storage
      syncSessionIdentity();
    },
    handleSessionWindowFocus() {
      // 从另一窗口点回来时再对一次（并排窗口常不触发 visibilitychange）
      syncSessionIdentity();
    },
    renewToken() {
      if (!this.hasLoginToken()) {
        this.stopTokenRenewTimer();
        return;
      }
      if (this.tokenRenewing) {
        return;
      }
      this.tokenRenewing = true;
      this.$axios
        .post(this.$store.state.serverUrl + "ytb/user/token/renew")
        .catch(() => {})
        .finally(() => {
          this.tokenRenewing = false;
          this.syncTokenRenewTimer();
        });
    },
    parseTruthy(value) {
      return value === true || value === "true" || value === 1 || value === "1";
    },
    getStoredUser() {
      try {
        return JSON.parse(window.localStorage.getItem("user") || "null");
      } catch (error) {
        return null;
      }
    },
    getFirstLoginFlag(user) {
      const storedFirstLogin = window.localStorage.getItem("firstLogin");
      if (storedFirstLogin !== null) {
        return this.parseTruthy(storedFirstLogin);
      }
      if (user && user.firstLogin != null) {
        return this.parseTruthy(user.firstLogin);
      }
      return Number(user && user.loginFlag) === 0;
    },
    checkFirstLoginAllocationPrompt() {
      if (this.isStandaloneAuthRoute) {
        this.showFirstLoginAllocationPrompt = false;
        return;
      }
      if (!window.localStorage.getItem("token")) {
        this.showFirstLoginAllocationPrompt = false;
        return;
      }
      const user = this.getStoredUser();
      // 仅企业超级管理员首次登录、且注册时确实赠送了积分才引导分配团队积分（个人用户、管理员及未赠送不展示）
      const shouldShow =
        user &&
        isSuperAdmin(user) &&
        this.getFirstLoginFlag(user) &&
        Number(user.clientType) === 1 &&
        this.hasRegisterGift();
      this.showFirstLoginAllocationPrompt = Boolean(shouldShow);
      if (shouldShow) {
        this.$nextTick(this.updateAllocationGuideTarget);
      }
    },
    /**
     * 登录成功后若有未读公告则弹窗；与首次分配引导互斥，后者优先
     */
    tryShowLoginAnnouncement() {
      if (this.isStandaloneAuthRoute) {
        return;
      }
      if (!this.hasLoginToken()) {
        return;
      }
      if (this.showFirstLoginAllocationPrompt) {
        return;
      }
      if (this.loginAnnouncementVisible) {
        return;
      }
      const announcement = peekPendingAnnouncement();
      if (!announcement) {
        return;
      }
      this.loginAnnouncement = announcement;
      this.loginAnnouncementVisible = true;
    },
    /** 用户确认公告后清除会话暂存，避免同会话重复弹出 */
    handleLoginAnnouncementConfirm() {
      clearPendingAnnouncement();
      this.loginAnnouncementVisible = false;
      this.loginAnnouncement = null;
    },
    /** 登录接口首登带回实际到账的注册赠送积分；旧版后端未返回时按有赠送处理，保持原有弹窗行为 */
    hasRegisterGift() {
      const stored = window.localStorage.getItem("registerGiftIntegral");
      if (stored === null || stored.trim() === "") {
        return true;
      }
      return Number(stored) > 0;
    },
    dismissFirstLoginAllocation() {
      window.localStorage.setItem("firstLogin", "false");
      window.localStorage.removeItem("registerGiftIntegral");
      this.showFirstLoginAllocationPrompt = false;
    },
    handleFirstLoginAllocate() {
      this.dismissFirstLoginAllocation();
      if (this.$route.path !== "/teamManagement") {
        this.$router.push("/teamManagement");
      }
    },
    updateBodyScrollLock() {
      if (typeof document === "undefined") {
        return;
      }
      if (this.showFirstLoginAllocationPrompt) {
        if (this.previousBodyOverflow === null) {
          this.previousBodyOverflow = document.body.style.overflow || "";
        }
        document.body.style.overflow = "hidden";
        return;
      }
      this.releaseBodyScrollLock();
    },
    releaseBodyScrollLock() {
      if (
        typeof document === "undefined" ||
        this.previousBodyOverflow === null
      ) {
        return;
      }
      document.body.style.overflow = this.previousBodyOverflow;
      this.previousBodyOverflow = null;
    },
    updateAllocationGuideState() {
      if (this.showFirstLoginAllocationPrompt) {
        this.$nextTick(() => {
          this.updateAllocationGuideTarget();
          this.addAllocationGuideResize();
        });
        return;
      }
      this.allocationGuideTargetStyle = null;
      this.removeAllocationGuideResize();
    },
    getAllocationGuideTarget() {
      if (typeof document === "undefined") {
        return null;
      }
      return (
        document.querySelector(".credits-btn-bar") ||
        document.querySelector(".points-trigger")
      );
    },
    updateAllocationGuideTarget() {
      const target = this.getAllocationGuideTarget();
      if (!target) {
        this.allocationGuideTargetStyle = null;
        return;
      }
      const rect = target.getBoundingClientRect();
      const paddingX = 6;
      const paddingY = 5;
      this.allocationGuideTargetStyle = {
        top: `${rect.top - paddingY}px`,
        left: `${rect.left - paddingX}px`,
        width: `${rect.width + paddingX * 2}px`,
        height: `${rect.height + paddingY * 2}px`,
      };
    },
    addAllocationGuideResize() {
      if (this.allocationGuideResizeHandler || typeof window === "undefined") {
        return;
      }
      this.allocationGuideResizeHandler = () => {
        this.updateAllocationGuideTarget();
      };
      window.addEventListener("resize", this.allocationGuideResizeHandler);
      window.addEventListener(
        "scroll",
        this.allocationGuideResizeHandler,
        true,
      );
    },
    removeAllocationGuideResize() {
      if (!this.allocationGuideResizeHandler || typeof window === "undefined") {
        return;
      }
      window.removeEventListener("resize", this.allocationGuideResizeHandler);
      window.removeEventListener(
        "scroll",
        this.allocationGuideResizeHandler,
        true,
      );
      this.allocationGuideResizeHandler = null;
    },
  },
};
</script>

<style lang="less">
.activity-promotion-spacer {
  width: 100%;
  height: var(--activity-banner-height, 48PX);
}

#app.has-activity-banner > .head,
#app.has-activity-banner > .home-page .home-header,
#app.has-activity-banner > .home-page .home-account-actions {
  top: var(--activity-banner-height, 48PX);
}

.first-login-allocation {
  position: fixed;
  inset: 0;
  z-index: 3000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
  background: rgba(0, 0, 0, 0.78);
}

.first-login-allocation__spotlight {
  position: fixed;
  z-index: 3001;
  box-sizing: border-box;
  border: 4px solid #7c3cff;
  border-radius: 999px;
  pointer-events: none;
  animation: first-login-spotlight-pulse 1.55s ease-in-out infinite;
  box-shadow: 0 0 0 1px rgba(168, 85, 247, 0.22),
    0 0 18px rgba(124, 60, 255, 0.55), 0 0 38px rgba(124, 60, 255, 0.36);
}

.first-login-allocation__spotlight::after {
  content: "";
  position: absolute;
  left: 50%;
  bottom: -24px;
  width: 0;
  height: 0;
  border-left: 16px solid transparent;
  border-right: 16px solid transparent;
  border-bottom: 22px solid #8a3ff4;
  filter: drop-shadow(0 7px 12px rgba(124, 60, 255, 0.5));
  transform: translateX(-50%);
  animation: first-login-arrow-bob 1.2s ease-in-out infinite;
}

.first-login-allocation__dialog {
  position: relative;
  z-index: 3002;
  width: 60vw;
  max-width: calc(100vw - 32px);
  min-width: 520px;
  padding: 48px 64px 86px;
  box-sizing: border-box;
  border-radius: 8px;
  border: 1px solid rgba(79, 83, 114, 0.72);
  background: #14151f;
  box-shadow: 0 28px 70px rgba(0, 0, 0, 0.58);
  color: #f8fafc;
  text-align: center;
  animation: first-login-dialog-in 0.26s ease-out;
}

.first-login-allocation__title {
  margin: 0 0 34px;
  color: #f7f4ff;
  font-size: 32px;
  font-weight: 700;
  line-height: 1.3;
}

.first-login-allocation__desc {
  max-width: 720px;
  margin: 0 auto;
  color: rgba(245, 243, 255, 0.88);

  font-weight: 600;
  line-height: 1.64;
}

.first-login-allocation__desc p {
  font-size: 20px;
  margin: 0;
}

.first-login-allocation__desc p + p {
  margin-top: 10px;
}

.first-login-allocation__emphasis {
  color: #ffd54a;
  font-size: 1.16em;
  font-weight: 800;
}

.first-login-allocation__primary,
.first-login-allocation__secondary {
  display: block;
  width: 100%;
  border: 0;
  font-family: inherit;
  cursor: pointer;
}

.first-login-allocation__primary {
  margin-top: 86px;
  height: 64px;
  border-radius: 7px;
  background: linear-gradient(90deg, #8a3ff4 0%, #7437ee 100%);
  color: #ffffff;
  font-size: 24px;
  font-weight: 700;
  box-shadow: 0 14px 28px rgba(118, 55, 238, 0.26);
}

.first-login-allocation__primary:hover,
.first-login-allocation__primary:focus {
  background: linear-gradient(90deg, #9652f7 0%, #7f44f1 100%);
}

.first-login-allocation__secondary {
  margin-top: 22px;
  background: transparent;
  color: rgba(245, 243, 255, 0.36);
  font-size: 20px;
  line-height: 26px;
}

.first-login-allocation__secondary:hover,
.first-login-allocation__secondary:focus {
  color: rgba(245, 243, 255, 0.64);
}

@keyframes first-login-spotlight-pulse {
  0%,
  100% {
    transform: scale(1);
    opacity: 0.9;
  }

  50% {
    transform: scale(1.08);
    opacity: 1;
  }
}

@keyframes first-login-arrow-bob {
  0%,
  100% {
    transform: translate(-50%, 0);
  }

  50% {
    transform: translate(-50%, 7px);
  }
}

@keyframes first-login-dialog-in {
  from {
    opacity: 0;
    transform: translateY(10px) scale(0.98);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@media (max-width: 520px) {
  .first-login-allocation__dialog {
    width: 100%;
    min-width: 0;
    min-height: 0;
    padding: 32px 22px 28px;
  }

  .first-login-allocation__title {
    font-size: 18px;
  }

  .first-login-allocation__desc {
    font-size: 14px;
    line-height: 1.65;
  }
}

/* 固定在视口底部，避免与 has-page 的 100vh 布局叠出整页滚动条；高度用 PX 避免 rem 漂移 */
.site-icp-footer {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  width: 100%;
  height: 36PX;
  min-height: 36PX;
  max-height: 36PX;
  padding: 0 16PX;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  background: #0d0e12;
  border-top: 1PX solid rgba(255, 255, 255, 0.08);
  text-align: center;
  line-height: 16PX;
}

.site-icp-link {
  color: rgba(245, 243, 255, 0.42);
  font-size: 12PX;
  text-decoration: none;
  word-break: break-word;

  &:hover {
    color: rgba(255, 255, 255, 0.82);
  }
}

.cornerMarkerStatus {
  position: absolute;
  top: 0px;
  z-index: 100;
  left: 0px;
}
.detect-search-select-dropdown {
  .el-select-dropdown__list {
    background: #1d1e1f !important;
  }
  .el-select-dropdown__item.selected {
    background-color: #262727;
  }
  .el-select-dropdown__item.hover,
  .el-select-dropdown__item:hover {
    background-color: #262727;
  }
}
.operationButton {
  background: #6f42ff !important;
  color: #fff !important;
  border-color: #6f42ff !important;
}
.has-page {
  width: 100%;
  // min-width: 1600px;
  height: 100%;
  //   height: calc(100vh - 142px);
  background: #0d0e12;
  display: flex;
  justify-content: center;
  align-items: center;
  //   margin: 35px 0 0;
	padding: 5.14286rem 4.5rem
    calc(2.14286rem + var(--site-icp-footer-height, 0PX));
}
.search {
  width: 100%;
  min-height: calc(
    100vh - 5.14286rem - 2.14286rem - var(--site-icp-footer-height, 0PX)
  );
  height: auto;
  background: #ffffff;
  border-radius: 16px 16px 16px 16px;
  justify-content: center;
  margin-top: 0;
  .searchBox {
    width: 884px;
    display: flex;
    flex-direction: column;
    align-items: center;
  }
}
/* 优化滚动条样式 */
::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

::-webkit-scrollbar-track {
  background: transparent; /* 更清爽 */
}

::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2); /* 半透明质感 */
  border-radius: 6px;
  transition: background 0.3s;
}

::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.4);
}

::-webkit-scrollbar-corner {
  background: transparent;
}
.radioButton {
  .el-radio {
    margin-right: 20px;
    .el-radio__input.is-checked + .el-radio__label {
      color: #5e7bfe; // 选中文字颜色
    }
    .el-radio__input.is-checked .el-radio__inner {
      border-color: #5e7bfe;
      background-color: #5e7bfe; // 选中圆点颜色
    }
    .el-radio__inner {
      border: 1px solid #dcdfe6;
    }
    .el-radio__label {
      font-weight: 500;
    }
  }
  .el-checkbox {
    margin-right: 20px;
    .el-checkbox__input.is-checked + .el-checkbox__label {
      color: #5e7bfe; // 选中文字颜色
    }
    .el-checkbox__input.is-checked .el-checkbox__inner {
      border-color: #5e7bfe;
      background-color: #5e7bfe; // 选中圆点颜色
    }
    .el-checkbox__inner {
      border: 1px solid #dcdfe6;
    }
    .el-checkbox__label {
      font-weight: 500;
    }
  }
}
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.3s ease;
  overflow: hidden;
}
.fade-slide-enter,
.fade-slide-leave-to {
  opacity: 0;
  max-height: 0;
}
.fade-slide-enter-to,
.fade-slide-leave {
  opacity: 1;
  max-height: 500px; /* 你可以根据内容设置更大值 */
}
.el-input .el-input__count,
.el-input .el-input__count .el-input__count-inner,
.el-textarea .el-input__count,
.el-textarea .el-input__count .el-input__count-inner,
.el-dialog .el-input__count,
.el-dialog .el-input__count-inner {
  background: transparent !important;
  background-color: transparent !important;
}

.el-message-box {
  background: #13141a !important;
  background-color: #13141a !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
  border-radius: 8px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.5);
}

.el-message-box__header,
.el-message-box__content,
.el-message-box__btns {
  background: #13141a !important;
  background-color: #13141a !important;
}

.el-message-box__title,
.el-message-box__title span {
  color: #fff !important;
}

.el-message-box__headerbtn .el-message-box__close {
  color: #8e94a3;

  &:hover {
    color: #fff;
  }
}

.el-message-box__content,
.el-message-box__message {
  color: #d1d5db;
}

.el-message-box__status.el-icon-warning {
  color: #f6ad3c;
}

.el-message-box__btns {
  .el-button {
    background: #24252e;
    border-color: rgba(255, 255, 255, 0.12);
    color: #cbd5e1;

    &:hover,
    &:focus {
      background: #2c2d36;
      border-color: rgba(139, 92, 246, 0.55);
      color: #fff;
    }
  }

  .el-button--primary {
    background: linear-gradient(135deg, #a855f7, #6366f1);
    border: none;
    color: #fff;

    &:hover,
    &:focus {
      background: linear-gradient(135deg, #b06bf8, #7477f5);
      color: #fff;
    }
  }
}

/* toast 挂到 body，跟确认框同一套暗色卡片，去掉 Element 默认浅绿条 */
.el-message {
  /* 覆盖 Element 默认 380px 宽条，改成随文案收紧 */
  min-width: 0 !important;
  max-width: min(28rem, calc(100vw - 2rem));
  padding: 10px 16px;
  background: #1c1d26 !important;
  border: 1px solid #2d2d3a !important;
  border-radius: 8px;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.45);
}

.el-message--success,
.el-message--warning,
.el-message--error,
.el-message--info {
  background-color: #1c1d26 !important;
  border-color: #2d2d3a !important;
}

.el-message__content {
  color: #e8e8ed !important;
  font-size: 14px;
  line-height: 1.5;
}

.el-message__icon {
  margin-right: 8px;
  font-size: 16px;
}

/* 图标沿用工作台状态色：成功绿 / 警告橙 / 错误红 / 信息紫 */
.el-message--success .el-message__icon,
.el-message .el-icon-success {
  color: #4ade80 !important;
}

.el-message--warning .el-message__icon,
.el-message .el-icon-warning {
  color: #f6ad3c !important;
}

.el-message--error .el-message__icon,
.el-message .el-icon-error {
  color: #f87171 !important;
}

.el-message--info .el-message__icon,
.el-message .el-icon-info {
  color: #a78bfa !important;
}

.el-message__closeBtn {
  color: #8e94a3;

  &:hover {
    color: #fff;
  }
}

/* 窄屏收紧边距，长文案在卡片内换行而不是撑出视口 */
@media (max-width: 520px) {
  .el-message {
    padding: 10px 12px;
  }

  .el-message__content {
    font-size: 13px;
  }
}
</style>
